import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CardDetailsBanner } from '@/common/components/CardDetailsBanner/CardDetailsBanner';
import { CardExpirationBanner } from '@/common/components/CardExpirationBanner/CardExpirationBanner';
import { PageLayout } from '@/common/components/PageLayout/PageLayout';
import { ActivitySection } from '@/common/containers/Dashboard/Home/ActivitySection/ActivitySection';
import { DiscountsSection } from '@/common/containers/Dashboard/Home/DiscountsSection/DiscountsSection';
import { HomeActions } from '@/common/containers/Dashboard/Home/HomeActions/HomeActions';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import {
  useCanDisplayCardDetailsBanner,
  useShouldShowCardExpirationBanner,
} from '@/common/integrations/menhir/employee/card/card.hooks';
import { Href } from 'expo-router';

export type THomeLayoutProps = {
  alertsSlot?: ReactNode;
  primaryActionsSlot?: ReactNode;
  pocketCardsSlot?: ReactNode;
  secondaryActionsSlot?: ReactNode;
  bottomSlot?: ReactNode;
  // TODO: This will probably die after we implement the new universal activity page
  activityRoute?: Href;
};

export function HomeLayout({
  alertsSlot,
  pocketCardsSlot,
  primaryActionsSlot,
  secondaryActionsSlot,
  bottomSlot,
  activityRoute,
}: THomeLayoutProps) {
  const { t } = useTranslation();
  const { company } = useEmployeeDetails();
  const { showExpirationBanner, isExpired } =
    useShouldShowCardExpirationBanner();
  const canDisplayCardDetailsBanner = useCanDisplayCardDetailsBanner();

  return (
    <PageLayout
      headerProps={{
        title: t('dashboard.home.title'),
        actions: <HomeActions />,
      }}
    >
      {alertsSlot}
      {primaryActionsSlot}
      {showExpirationBanner && (
        <CardExpirationBanner
          isExpired={isExpired}
          isDigitalRequestFormatRenewal={
            company.settings.cardRequestFormat === 'digital'
          }
        />
      )}
      {pocketCardsSlot}
      {canDisplayCardDetailsBanner && !showExpirationBanner && (
        <CardDetailsBanner />
      )}
      {secondaryActionsSlot}
      <ActivitySection seeAllRoute={activityRoute} />
      <DiscountsSection />
      {bottomSlot}
    </PageLayout>
  );
}
